/**
 * Resuelve FPS de cine desde tags DICOM / meta de instancia / descripción de serie.
 * Prioridad: Recommended Display Frame Rate → Cine Rate → Frame Time →
 * Frame Time Vector → campos de manifest → regex en seriesDescription.
 */

const FPS_MIN = 1
const FPS_MAX = 60

/**
 * @param {unknown} v
 * @returns {number|null}
 */
function toFiniteNumber(v) {
  if (v == null || v === '') return null
  const n = typeof v === 'number' ? v : parseFloat(String(v).trim().replace(',', '.'))
  return Number.isFinite(n) ? n : null
}

/**
 * @param {number|null|undefined} fps
 * @returns {number|null}
 */
export function clampCineFps(fps) {
  const n = toFiniteNumber(fps)
  if (n == null || !(n > 0)) return null
  const rounded = Math.round(n)
  if (rounded < FPS_MIN || rounded > FPS_MAX) return null
  return rounded
}

/**
 * Frame Time (ms) → FPS.
 * @param {number|null|undefined} frameTimeMs
 * @returns {number|null}
 */
export function fpsFromFrameTimeMs(frameTimeMs) {
  const ft = toFiniteNumber(frameTimeMs)
  if (ft == null || !(ft > 0)) return null
  return clampCineFps(1000 / ft)
}

/**
 * @param {string|null|undefined} text
 * @returns {number|null}
 */
export function fpsFromSeriesDescription(text) {
  const s = String(text || '')
  if (!s) return null
  // "Left Coronary 15 fps", "15fps", "15 FPS", "Cine 7.5 f/s"
  const m = s.match(/(\d+(?:\.\d+)?)\s*(?:fps|f\/s|frames?\s*\/\s*s)/i)
  if (!m) return null
  return clampCineFps(m[1])
}

/**
 * Lee un tag string/numérico de dicom-parser DataSet.
 * @param {object|null|undefined} dataSet
 * @param {string} tag
 * @returns {string}
 */
function dsString(dataSet, tag) {
  if (!dataSet || typeof dataSet.string !== 'function') return ''
  try {
    return String(dataSet.string(tag) || '').trim()
  } catch (e) {
    return ''
  }
}

/**
 * @param {object|null|undefined} dataSet
 * @returns {{ recommendedDisplayFrameRate: number|null, cineRate: number|null, frameTime: number|null, frameTimeVectorMean: number|null }}
 */
export function readCineTimingFromDataset(dataSet) {
  const recommendedDisplayFrameRate = toFiniteNumber(dsString(dataSet, 'x00082144'))
  const cineRate = toFiniteNumber(dsString(dataSet, 'x00180040'))
  const frameTime = toFiniteNumber(dsString(dataSet, 'x00181063'))
  let frameTimeVectorMean = null
  const ftv = dsString(dataSet, 'x00181065')
  if (ftv) {
    const parts = ftv
      .split('\\')
      .map((x) => toFiniteNumber(x))
      .filter((n) => n != null && n > 0)
    if (parts.length) {
      frameTimeVectorMean =
        parts.reduce((a, b) => a + b, 0) / parts.length
    }
  }
  return {
    recommendedDisplayFrameRate,
    cineRate,
    frameTime,
    frameTimeVectorMean,
  }
}

/**
 * Meta suelta de file item / manifest instance.
 * @param {object|null|undefined} file
 * @returns {object}
 */
function metaFromFile(file) {
  if (!file || typeof file !== 'object') return {}
  const series = file.series && typeof file.series === 'object' ? file.series : {}
  return {
    recommendedDisplayFrameRate:
      file.recommendedDisplayFrameRate ??
      file.RecommendedDisplayFrameRate ??
      series.recommendedDisplayFrameRate,
    cineRate: file.cineRate ?? file.CineRate ?? series.cineRate,
    frameTime: file.frameTime ?? file.FrameTime ?? series.frameTime,
    frameRate: file.frameRate ?? file.fps ?? series.frameRate ?? series.fps,
    seriesDescription:
      series.seriesDescription ||
      series.SeriesDescription ||
      file.seriesDescription ||
      file.SeriesDescription ||
      '',
  }
}

/**
 * @param {{
 *   image?: object|null,
 *   dataSet?: object|null,
 *   file?: object|null,
 *   seriesDescription?: string|null,
 *   defaultFps?: number|null,
 * }} [opts]
 * @returns {{ fps: number, source: string }}
 */
export function resolveCineFps(opts = {}) {
  const fallback = clampCineFps(opts.defaultFps) || 30
  const dataSet =
    opts.dataSet ||
    (opts.image && opts.image.data) ||
    null
  const fromDs = readCineTimingFromDataset(dataSet)
  const fromFile = metaFromFile(opts.file)
  const desc =
    opts.seriesDescription ||
    fromFile.seriesDescription ||
    ''

  const candidates = [
    {
      source: 'recommendedDisplayFrameRate',
      fps: clampCineFps(
        fromDs.recommendedDisplayFrameRate ??
          fromFile.recommendedDisplayFrameRate
      ),
    },
    {
      source: 'cineRate',
      fps: clampCineFps(fromDs.cineRate ?? fromFile.cineRate),
    },
    {
      source: 'frameTime',
      fps: fpsFromFrameTimeMs(fromDs.frameTime ?? fromFile.frameTime),
    },
    {
      source: 'frameTimeVector',
      fps: fpsFromFrameTimeMs(fromDs.frameTimeVectorMean),
    },
    {
      source: 'manifestFrameRate',
      fps: clampCineFps(fromFile.frameRate),
    },
    {
      source: 'seriesDescription',
      fps: fpsFromSeriesDescription(desc),
    },
  ]

  for (let i = 0; i < candidates.length; i++) {
    const c = candidates[i]
    if (c.fps != null) return { fps: c.fps, source: c.source }
  }
  return { fps: fallback, source: 'default' }
}
